/**
 * Auto-generated from @intelishs-group/schema-core v4.25.13
 * Source: recursoshumanosTypes — Recursos Humanos — EmpleadoController (expediente, contactos, estudios, pagadoras, documentos)
 * Generated: 2026-09-17
 * Do not edit manually. Run: npm run generate:services
 */
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiBaseService } from './api-base.service';
import type {
  ChangeAreaRequest,
  ContactoEmergenciaDto,
  ContactoEmergenciaDtoResponseDto,
  CreateEmpleadoRequest,
  EmpleadoCatalogosResponseResponseDto,
  EmpleadoDocumentoDtoListResponseDto,
  EmpleadoListResponseListResponseDto,
  EmpleadoResponseResponseDto,
  EstudioDto,
  EstudioDtoResponseDto,
  ExisteIdInternoResponseResponseDto,
  PagadoraDto,
  PagadoraDtoResponseDto,
  ProblemDetails,
  RolCanonicoResponse,
  SiguienteIdInternoResponseResponseDto,
  UpdateEmpleadoRequest,
} from '@schemas/rh-empleado';

@Injectable({ providedIn: 'root' })
export class RhEmpleadoService extends ApiBaseService {
  private baseUrl = `${environment.BaseUrl}/Empleado`;

  /** Obtener lista de empleados. Sin parámetro retorna todos. Usar activo=true para activos, activo=false para inactivos. */
  async getEmpleado(activo?: boolean) {
    const params: Record<string, string> = {};
    if (activo !== undefined) params['activo'] = activo.toString();
    return this.get<EmpleadoListResponseListResponseDto>(`${this.baseUrl}`, params);
  }

  /** Crear un nuevo empleado (alta vía SP + siembra de filas esqueleto en las tablas `mov*`: ingreso, domicilio, horario, contrato). */
  async createEmpleado(body: CreateEmpleadoRequest) {
    return this.post<EmpleadoResponseResponseDto>(`${this.baseUrl}`, body);
  }

  /** Devuelve el rol canónico del usuario logueado (Admin/RH/Director/Empleado). El claim ya lo inyectó `OnTokenValidated` del JwtBearer (mapeo directo de Intellishs.SINAY.Recursoshumanos.Domain.Authorization.RoleMapping o, para labels ambiguos del portal como `"Mi Portal RH"`, consulta a `rh.movempleadojerarquia`). Endpoint espejo de `/api/Membership/MyRole` pero alojado aquí porque `/api/Empleado/**` está ruteado en el gateway YARP de todos los ambientes (no siempre lo está `/api/Membership/MyRole`, que puede quedar fuera si el gateway tiene rutas explícitas en vez de wildcard). El frontend (session-bootstrap) lo consume para sobrescribir `session.role` con el canónico antes de armar el menú. */
  async me_role() {
    return lastValueFrom(this.http.get<RolCanonicoResponse>(`${this.baseUrl}/me/role`));
  }

  /** Obtener el empleado RH del usuario actualmente logueado. Resuelve el empleado a partir del claim del JWT que contiene el kidusuario (public.catusuario) y hace el JOIN con rh.catempleadorh vía el campo fkidempleadosistema. */
  async me() {
    return this.get<EmpleadoResponseResponseDto>(`${this.baseUrl}/me`);
  }

  /** Obtener un empleado por ID con todas sus secciones. Lectura completa de un empleado ajeno solo para roles privilegiados (la pantalla de detalle es accesible exclusivamente desde RH/Admin/Director). */
  async getEmpleadoById(id: number) {
    return this.get<EmpleadoResponseResponseDto>(`${this.baseUrl}/${id}`);
  }

  /** Actualizar un empleado existente (upsert sobre las tablas `mov*`). */
  async updateEmpleadoById(id: number, body: UpdateEmpleadoRequest) {
    return this.put<EmpleadoResponseResponseDto>(`${this.baseUrl}/${id}`, body);
  }

  /** Desactivar un empleado (soft delete). */
  async deleteEmpleadoById(id: number) {
    return lastValueFrom(this.http.delete<ProblemDetails>(`${this.baseUrl}/${id}`));
  }

  /** Verifica si una clave interna (MC####) está disponible o ya en uso por otro empleado. Lo consume el validador async del formulario de RH mientras el usuario tipea para advertir antes del submit. Si está editando un empleado, pasar `excludeId` con su ID para no contar su propia clave como duplicado. */
  async checkInternalId(idInterno?: string, excludeId?: number) {
    const params: Record<string, string> = {};
    if (idInterno !== undefined) params['idInterno'] = idInterno;
    if (excludeId !== undefined) params['excludeId'] = excludeId.toString();
    return this.get<ExisteIdInternoResponseResponseDto>(
      `${this.baseUrl}/check-internal-id`,
      params,
    );
  }

  /** Sugiere el siguiente consecutivo disponible con el prefijo indicado (default MC). Lo consume el formulario de RH cuando el usuario tipea una clave ya tomada — la usa para ofrecer un clic-para-aplicar bajo el aviso "Este ID ya está en uso por otro empleado". */
  async nextInternalId(prefijo?: string) {
    const params: Record<string, string> = {};
    if (prefijo !== undefined) params['prefijo'] = prefijo;
    return this.get<SiguienteIdInternoResponseResponseDto>(
      `${this.baseUrl}/next-internal-id`,
      params,
    );
  }

  /** Activar un empleado (reingreso). Aún no implementado en el servicio — devuelve 422 hasta que el DBA proporcione el SP de reingreso. */
  async activate(id: number) {
    return lastValueFrom(this.http.patch<ProblemDetails>(`${this.baseUrl}/${id}/activate`, null));
  }

  /** Cambiar área y dirección de un empleado. Sin histórico — sobreescribe la fila vigente en `rh.relempleadodireccionarea`. Lo dispara el modal "Cambiar Área" desde el dropdown de acciones de la lista. */
  async area(id: number, body: ChangeAreaRequest) {
    return this.patch<EmpleadoResponseResponseDto>(`${this.baseUrl}/${id}/area`, body);
  }

  /** Catálogos dinámicos: áreas, puestos y direcciones distintas de los empleados en BD. Usado por el formulario de empleado para mostrar opciones actualizadas. */
  async catalogos() {
    return this.get<EmpleadoCatalogosResponseResponseDto>(`${this.baseUrl}/catalogos`);
  }

  /** Agrega un estudio al empleado. Devuelve la fila con su `id`. */
  async createEstudios(id: number, body: EstudioDto) {
    return this.post<EstudioDtoResponseDto>(`${this.baseUrl}/${id}/estudios`, body);
  }

  /** Actualiza un estudio existente. */
  async updateEstudios(id: number, kidEstudio: number, body: EstudioDto) {
    return this.put<EstudioDtoResponseDto>(`${this.baseUrl}/${id}/estudios/${kidEstudio}`, body);
  }

  /** Baja lógica (`bactivo=false`) de un estudio. */
  async deleteEstudios(id: number, kidEstudio: number) {
    return lastValueFrom(
      this.http.delete<ProblemDetails>(`${this.baseUrl}/${id}/estudios/${kidEstudio}`),
    );
  }

  /** Agrega una pagadora al empleado. Si `fechaAltaImss` llega null, el backend la setea a la fecha de hoy en CDMX (`FechasMexico.Hoy()`). */
  async createPagadoras(id: number, body: PagadoraDto) {
    return this.post<PagadoraDtoResponseDto>(`${this.baseUrl}/${id}/pagadoras`, body);
  }

  /** Actualiza una pagadora existente. */
  async updatePagadoras(id: number, kidPagadora: number, body: PagadoraDto) {
    return this.put<PagadoraDtoResponseDto>(`${this.baseUrl}/${id}/pagadoras/${kidPagadora}`, body);
  }

  /** Baja lógica (`bactivo=false`) de una pagadora. */
  async deletePagadoras(id: number, kidPagadora: number) {
    return lastValueFrom(
      this.http.delete<ProblemDetails>(`${this.baseUrl}/${id}/pagadoras/${kidPagadora}`),
    );
  }

  /** Agrega un contacto de emergencia al empleado. Devuelve la fila con `id` (kidempleadocontacto) asignado. */
  async createContactos(id: number, body: ContactoEmergenciaDto) {
    return this.post<ContactoEmergenciaDtoResponseDto>(`${this.baseUrl}/${id}/contactos`, body);
  }

  /** Actualiza un contacto de emergencia existente. */
  async updateContactos(id: number, kidContacto: number, body: ContactoEmergenciaDto) {
    return this.put<ContactoEmergenciaDtoResponseDto>(
      `${this.baseUrl}/${id}/contactos/${kidContacto}`,
      body,
    );
  }

  /** Baja lógica (`bactivo = false`) de un contacto de emergencia. */
  async deleteContactos(id: number, kidContacto: number) {
    return lastValueFrom(
      this.http.delete<ProblemDetails>(`${this.baseUrl}/${id}/contactos/${kidContacto}`),
    );
  }

  /** Lista las referencias de documentos del empleado (tipo + kiddocumento). */
  async getDocumentos(id: number) {
    return this.get<EmpleadoDocumentoDtoListResponseDto>(`${this.baseUrl}/${id}/documentos`);
  }

  /** Sube un documento del empleado al FileManager (S3) y guarda su referencia. multipart/form-data: campo `tipo` (kidtipodocumento) y `file` (el archivo). Devuelve la lista actualizada de documentos. Formatos permitidos: PDF, JPG, JPEG, PNG, XLSX, XLS, DOCX, DOC. */
  async createDocumentos(id: number, body: FormData) {
    return lastValueFrom(
      this.http.post<EmpleadoDocumentoDtoListResponseDto>(`${this.baseUrl}/${id}/documentos`, body),
    );
  }

  /** Elimina (borrado lógico) un documento del empleado en el FileManager y quita su referencia. Devuelve la lista actualizada. */
  async deleteDocumentos(id: number, kidDocumento: number) {
    return this.delete<EmpleadoDocumentoDtoListResponseDto>(
      `${this.baseUrl}/${id}/documentos/${kidDocumento}`,
    );
  }
}
